import { Flex, Text, SimpleGrid, Input, Icon } from "@chakra-ui/react";
import { RootState, useAppSelector } from "../../../store";
import { useEffect, useState } from "react";
import { transformGuides } from "../../../helpers/transformGuides";
import { FormikProps } from "formik";
import { IUpdateProjectBody } from "../../../api/projects/types";
import { BaseInput } from "../../Controls/BaseInput";
import { BaseTextArea } from "../../Controls/BaseTextArea";
import { BaseSelect, SelectOption } from "../../Controls/BaseSelect";
import { FormWrapper } from "../../FormWrapper/FormWrapper";
import { Customer } from "@/model/customers";
import DatePicker from "react-datepicker";
import { ru } from "date-fns/locale";
import "react-datepicker/dist/react-datepicker.css";
import { ProjectForm } from "../../../model/projects";
import { ViewInformationBlock } from "@/components/ViewInformationBlock/ViewInformationBlock";
import { FiFolder } from "react-icons/fi";
import api from "@/api";

interface UpdateProjectBlockProps {
  formik: FormikProps<ProjectForm>;
}

export const UpdateProjectBlock = ({ formik }: UpdateProjectBlockProps) => {
  const { currentProject } = useAppSelector(
    (state: RootState) => state.projects
  );
  const { guides } = useAppSelector(
    (state: RootState) => state.guides
  );
  const { customers } = useAppSelector(
    (state: RootState) => state.customers
  );
  const [recruitersOptions, setRecruitersOptions] = useState<SelectOption[]>([])

  useEffect(() => {
    api.recruiters.recruiters({ customer: true }).then((res) => {
      const resultRecruiters = res.data.map((el: any) => ({
        value: el.id,
        label: el.username,
      }))
      setRecruitersOptions(resultRecruiters)
    })
  }, [])

  const [statusList, setStatusList] = useState<SelectOption[]>([])
  const [legalEntityOptions, setLegalEntityOptions] = useState<SelectOption[]>([])
  const [projectFormatOptions, setProjectFormatOptions] = useState<SelectOption[]>([])

  const customerOptions: SelectOption[] = customers.items.map((el: Customer) => {
    const value = el.id !== undefined && typeof el.id === 'number' ? el.id : undefined;
    const label = el.name !== undefined && typeof el.name === 'string' ? el.name : undefined;

    return {
      value: value,
      label: label || '',
    };
  });

  useEffect(() => {
    if (guides) {
      const resultStatusList = transformGuides(guides.ProjectStatus)
      setStatusList(resultStatusList)
      const legals: SelectOption[] = guides?.LegalEntity ? Object.entries(guides.LegalEntity).map(([value, label]: any) => ({ value: Number(value), label: String(label) })) : [];
      const formats: SelectOption[] = guides?.ProjectFormat ? Object.entries(guides.ProjectFormat).map(([value, label]: any) => ({ value: Number(value), label: String(label) })) : [];
      
      setLegalEntityOptions(legals);
      setProjectFormatOptions(formats);
    }
  }, [guides])

  return (
    <ViewInformationBlock icon={<Icon as={FiFolder as any} boxSize={5} color="#3b515e" />} title="Основная информация">
      <Flex w="100%" gap="24px" flexDirection="column">
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
          <BaseInput
            fieldTitle={"Название проекта"}
            requiredField={true}
            value={formik.values.name}
            onChange={formik.handleChange}
            name="name"
          />
          <BaseSelect
            fieldTitle="Заказчик"
            requiredField={true}
            name="customer_id"
            options={customerOptions}
            placeholder="Выберите заказчика"
            value={formik.values.customer_id}
            onChange={(value) => {
              formik.setFieldValue("customer_id", value ? Number(value) : null)
            }}
            isEmpty={formik.values.customer_id === null}
          />
        </SimpleGrid>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
          <BaseSelect
            fieldTitle="Юр. лицо"
            requiredField={true}
            name="legal_entity_id"
            options={legalEntityOptions}
            placeholder="Выберите юр. лицо"
            value={(formik.values as any).legal_entity_id}
            onChange={(value) => {
              formik.setFieldValue("legal_entity_id", value ? Number(value) : null)
            }}
          />
          <BaseSelect
            fieldTitle="Статус проекта"
            requiredField={true}
            name="status"
            options={statusList}
            placeholder="Выберите статус"
            value={formik.values.status}
            onChange={(value) => {
              formik.setFieldValue("status", value || null)
            }}
            isEmpty={formik.values.status === null}
          />
          <BaseSelect
            fieldTitle="Формат работ"
            requiredField={true}
            name="project_format_id"
            options={projectFormatOptions}
            placeholder="Выберите формат"
            value={(formik.values as any).project_format_id}
            onChange={(value) => {
              formik.setFieldValue("project_format_id", value ? Number(value) : null)
            }}
          />
        </SimpleGrid>
        <BaseTextArea
            fieldTitle={"Описание проекта"}
            id="description"
            value={formik.values.description}
            onChange={formik.handleChange}
          />
          <BaseTextArea
            fieldTitle={"Комментарий"}
            id="comments"
            value={formik.values.comments}
            onChange={formik.handleChange}
          />
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
          <BaseInput
            fieldTitle={"Контактные данные"}
            name="contact"
            value={(formik.values as any).contact || ""}
            onChange={formik.handleChange}
          />
        </SimpleGrid>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
          <BaseInput
            fieldTitle={"Ссылка на проект (Bitrix 24)"}
            name="bitrix_project_link"
            value={(formik.values as any).bitrix_project_link || ""}
            onChange={formik.handleChange}
          />
          <BaseInput
            fieldTitle={"Ссылка на договор (Bitrix 24)"}
            name="bitrix_contract_link"
            value={(formik.values as any).bitrix_contract_link || ""}
            onChange={formik.handleChange}
          />
        </SimpleGrid>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
          <Flex flexDirection="column" gap="8px">
            <Text fontSize="sm" fontWeight="medium" color="gray.700" display="flex" alignItems="center" gap={1}>Дата старта</Text>
            <DatePicker
              selected={(formik.values as any).start_at ? new Date(Number((formik.values as any).start_at) * 1000) : null}
              onChange={(date: Date | null) => formik.setFieldValue("start_at", date ? Math.floor(date.getTime() / 1000) : undefined)}
              customInput={<Input /> as any}
              dateFormat="dd.MM.yyyy"
              locale={ru as unknown as string}
              placeholderText="дд.мм.гггг"
              isClearable
            />
          </Flex>
          <Flex flexDirection="column" gap="8px">
            <Text fontSize="sm" fontWeight="medium" color="gray.700" display="flex" alignItems="center" gap={1}>Дата планового завершения</Text>
            <DatePicker
              selected={(formik.values as any).planned_end_at ? new Date(Number((formik.values as any).planned_end_at) * 1000) : null}
              onChange={(date: Date | null) => formik.setFieldValue("planned_end_at", date ? Math.floor(date.getTime() / 1000) : undefined)}
              customInput={<Input /> as any}
              dateFormat="dd.MM.yyyy"
              locale={ru as unknown as string}
              placeholderText="дд.мм.гггг"
              isClearable
            />
          </Flex>
        </SimpleGrid>
        <BaseSelect
          fieldTitle="Ответственный аккаунт-менеджер"
          requiredField={true}
          name="responsible_user_id"
          options={recruitersOptions}
          placeholder="Выберите ответственного"
          value={formik.values.responsible_user_id}
          onChange={(value) => {
            formik.setFieldValue("responsible_user_id", value ? Number(value) : null)
          }}
          isEmpty={formik.values.responsible_user_id === null}
        />
      </Flex>
    </ViewInformationBlock>
  );
};
